import {ChangeDetectionStrategy, Component, inject, Input, OnChanges, OnDestroy, SimpleChanges} from '@angular/core';
import {PostfachFolderListComponent} from '../postfach-folder-list/postfach-folder-list.component';
import {PostfachFilterComponent} from '../postfach-filter/postfach-filter.component';
import {PostfachHeaderSearchComponent} from '../postfach-header-search/postfach-header-search.component';
import {Rechte} from '../../core/model/rechte.model';
import {NachrichtenOrdner} from '../../core/model/nachrichten-ordner';
import {NavigationService} from '../../shared/utils/navigation.service';
import {NachrichtenService} from '../../core/services/apiservices/nachrichten.service';
import {NachrichtSummary} from '../../core/model/nachricht/nachricht';
import {NachrichtenFilter} from '../../core/model/nachrichten-filter';
import {OrdnerEnum} from '../../core/model/ordner.enum';
import {environment} from '../../../environments/environment';
import {Subject, takeUntil} from 'rxjs';
import {NachrichtenTypeEnum} from '../../core/model/nachricht/nachrichten-type.enum';
import {getPostfachFormat} from '../../shared/utils/date-time.helper';
import {I18NextPipe} from '@kokos-i18next';
import {PostfachFeedbackComponent} from '../postfach-feedback/postfach-feedback.component';
import {APP_ROUTES} from '../../route-constants';
import {PostfachNachrichtListComponent} from '../postfach-nachricht-list/postfach-nachricht-list.component';
import {KundenTypeEnum} from '../../core/model/kunden-type.enum';

@Component({
    selector: 'kokos-postfach-content',
    imports: [
        PostfachFolderListComponent,
        PostfachFilterComponent,
        PostfachHeaderSearchComponent,
        PostfachNachrichtListComponent,
        I18NextPipe,
        PostfachFeedbackComponent
    ],
    templateUrl: './postfach-content.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
})
export class PostfachContentComponent implements OnChanges, OnDestroy {
    @Input() benutzerRechte: Rechte;
    @Input() nachrichtenOrdner: NachrichtenOrdner[] = [];
    @Input() selectedFolder = OrdnerEnum.ERHALTEN;
    @Input() postfachRestricted = false;

    private readonly nav = inject(NavigationService);
    private readonly nachrichtenService = inject(NachrichtenService);

    showAlleNachrichten = false;
    nachrichtenListeVoll: NachrichtSummary[] = [];
    filter = NachrichtenFilter.ALL;
    sucheValue = '';
    profilEinstellenLink = environment.profilEinstellenUrl;
    readonly MAX_NACHRICHTEN = 10;
    isLoading = false;

    protected readonly KundenTypeEnum = KundenTypeEnum;
    private readonly destroy$ = new Subject<void>();

    ngOnChanges(changes: SimpleChanges): void {
        const selectedFolderChange = changes['selectedFolder'];
        if (
            selectedFolderChange?.currentValue &&
            selectedFolderChange.currentValue !== selectedFolderChange.previousValue
        ) {
            this.loadNachrichtenForFolder(selectedFolderChange.currentValue);
        }
    }

    navigateToEditor(): void {
        this.nav.goTo(['/' + APP_ROUTES.NEU]);
    }

    navigateToFolder(selectedFolder: OrdnerEnum): void {
		this.loadNachrichtenForFolder(selectedFolder);
        this.nav.goTo(['/ordner', selectedFolder]);
    }

    setFilter(id: NachrichtenFilter): void {
        this.filter = id;
    }

    onSearchInput(searchInput: string): void {
        if (this.sucheValue !== searchInput) {
            this.sucheValue = searchInput;
        }
    }

    getNachrichenToShow(): NachrichtSummary[] {
        const filteredNachrichten = this.getFilteredNachrichten();
        return this.showAlleNachrichten
            ? filteredNachrichten
            : filteredNachrichten.slice(0, this.MAX_NACHRICHTEN);
    }

    showAllNachrichten(): void {
        this.showAlleNachrichten = true;
    }

    getFilteredNachrichtCount(): number {
        return this.getFilteredNachrichten().length;
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private loadNachrichtenForFolder(selectedFolder: OrdnerEnum): void {
        this.isLoading = true;
        this.nachrichtenService.getNachrichtenFromOrdner(selectedFolder.toLowerCase())
            .pipe(takeUntil(this.destroy$))
            .subscribe(nachrichten => {
                this.nachrichtenListeVoll = nachrichten ?? this.nachrichtenListeVoll;
                this.isLoading = false;
            });
    }

    private getFilteredNachrichten(): NachrichtSummary[] {
        return this.nachrichtenListeVoll.filter((nachricht) =>
            (
                this.filter === NachrichtenFilter.ALL ||
                (nachricht.typ === NachrichtenTypeEnum.JC && this.filter === NachrichtenFilter.JC) ||
                (nachricht.typ === NachrichtenTypeEnum.AA && this.filter === NachrichtenFilter.AA)
            ) &&
            this.matchesSearch(nachricht)
        );
    }

    private matchesSearch(nachricht: NachrichtSummary): boolean {
        const searchValue = this.sucheValue?.toLowerCase() ?? '';

        return (
            nachricht.betreff.toLowerCase().includes(searchValue) ||
            nachricht.bezug.toLowerCase().includes(searchValue) ||
            getPostfachFormat(nachricht.datum).toLowerCase().includes(searchValue)
        );
    }
}